import React, { useEffect, useState, useCallback, useMemo } from 'react';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import axios from '../api/apiClient';
import { fetchAvailableSlot, fetchAllSlot } from '../api/subscriptionApi';
import { token, domain } from './../components/Security';
import { toLowerUnderscore } from './../utils/textUtils';
import { CreateSearchBarWithPlaceHolder } from '../components/CommonObjects';
import { CreateTableForEmployeeSelect } from '../components/ManageTable';
import { WorkflowStatus } from '../utils/enums';
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SearchIcon from '@mui/icons-material/Search';
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import loadingSpinner from './../assets/loading.gif';
import { format } from 'date-fns';

// 1. IMPORT IMAGE ZOOM
import { ImageZoom } from './ImageViewer';
import { cn } from '../utils/cn';

//#region Table
const getNestedValue = (tableName, obj, path, actions) => {
  const value = path.split('.').reduce((acc, part) => {
    if (!acc) return undefined;

    // Handle array access like 'employeeinformations[0]'
    const match = part.match(/^(\w+)\[(\d+)\]$/);
    if (match) {
      const [, arrayKey, index] = match;
      return acc[arrayKey]?.[parseInt(index, 10)];
    }

    return acc[part];
  }, obj);

  // --- Handle specific formatting/conditions ---
  if (path === 'isactive' || path === 'isActive') {
    return value ? 'Active' : 'Inactive';
  }

  if (path === 'errors') {
    if (value) {
      const formattedErrors = value.replace(/\\n/g, '\n');

      return (
        <pre className="max-h-[100px] whitespace-pre-wrap text-red-500">
          {formattedErrors}
        </pre>
      );
    }
  }

  if (path === 'IsActive') {
    return value === 1 ? 'True' : 'False';
  }

  if (path === 'hasCompleted') {
    return value ? 'Yes' : 'No';
  }

  if (path === 'employeeinformations.status') {
    return value ? 'Approved' : 'Unapproved';
  }

  if (path === 'timefrom' || path === 'timeto') {
    if (!value) return '-';
    const date = new Date(value);
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  if (path === 'isRequester'
    || path === 'isFullLoanAccess'
    || path === 'isProcessor'
    || path === 'isEmployeeRequestApprover'
    || path === 'isEmployeeRequestRequester'
    || path === 'isAssignmentManager'
    || path === 'isCompensationManager'
    || path === 'isAssignedEmployee'
    || path === 'isEncashable'
  ) {
    return value === true ? 'True' : 'False';
  }


  // Dates Display
  if (['DateFrom', 'DateTo', 'date', 'datefrom', 'dateFrom', 'dateto', 'dateTo', 'dateofbirth', 'DateOfBirth', 'requestedDate', 'cancelledDate', 'processingDate'].includes(path)) {
    if (!value) return '-';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '-';
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  // 2. UPDATED COMPANY LOGO TO USE IMAGEZOOM
  if (tableName === 'Company' && path === 'logo') {
    if (!value) {
      return <></>
    }
    const imageSrc = 'data:image/jpeg;base64,' + value;
    return <ImageZoom src={imageSrc} alt="Company Logo" imageClassName="max-h-[40px]" />
  }

  if (tableName === 'EmployeeMemo' && path === 'content') {
    if (!value) {
      return <></>
    }
    const imageSrc = 'data:image/jpeg;base64,' + value;
    return <ImageZoom src={imageSrc} alt="Memo Image" imageClassName="max-h-[40px]" />
  }

  if ((tableName === 'OnBoarding' || tableName === 'OutBoarding') && path === 'file') {
    if (!value) {
      return <>No uploaded file</>
    }

    const foundAction = actions.find(i => i.name === 'Download');
    return (
      <button
        className="cursor-pointer rounded-md border-none bg-sky-600 p-[1px] text-white hover:bg-sky-700"
        title="Download"
        onClick={() => foundAction.function(obj)}
      >
        {foundAction.icon}
      </button>
    );
  }

  if (tableName === 'TrainingAndSeminars' && path === 'file') {
    const newFilename = toLowerUnderscore(obj.title);
    return (newFilename ?? '-');
  }

  if (tableName === 'EmployeeSchedule' && path === 'employeeshiftschedules') {
    const foundAction = actions.find(i => i.name === 'View');
    return (
      <button
        className="rounded-md bg-sky-600 p-[1px] text-white hover:bg-sky-700"
        title="View Schedule"
        onClick={() => foundAction.function(obj)}
      >
        {foundAction.icon}
      </button>
    );
  }

  // 3. UPDATED CALENDAR IMAGE TO USE IMAGEZOOM
  if (tableName === 'Calendar' && path === 'image') {
    if (!value) {
      return <></>
    }
    const imageSrc = 'data:image/jpeg;base64,' + value;
    return <ImageZoom src={imageSrc} alt="Calendar Image" />
  }

  if (tableName === 'HolidayType') {
    if (path === 'payrateifnotworked' || path === 'payrateifworked') {
      return `${value}%`;
    }
  }

  if (tableName === 'Holiday' && path === 'holidaylocations') {
    if (obj.isalllocation) {
      return <span>All locations</span>;
    }
    return (
      <span>
        {Array.isArray(value) && value.length > 0
          ? value.map(loc => loc.locations.description).join(', ')
          : '-'}
      </span>
    );
  }

  if (tableName === "MRF" && path === "status") {
    const statusStyles = {
      [WorkflowStatus.PENDING]: "bg-orange-200 text-orange-800",
      [WorkflowStatus.APPROVED]: "bg-blue-200 text-blue-800",
      [WorkflowStatus.DECLINED]: "bg-red-200 text-red-800",
      [WorkflowStatus.COMPLETED]: "bg-green-200 text-green-800",
      [WorkflowStatus.CANCELLED]: "bg-red-300 text-red-800",
      [WorkflowStatus.PROCESSING]: "bg-blue-200 text-blue-700",
      [WorkflowStatus.ONBOARDING]: "bg-blue-300 text-blue-800",
    };

    return (
      <span className={`px-2 py-1 rounded-md font-medium ${statusStyles[value] || "bg-gray-200 text-gray-700"}`}>
        {value}
      </span>
    );
  }

  const formatDate = (value) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  if ((tableName === 'Overtime'
    || tableName === 'DTRCorrection'
    || tableName === 'Leave'
    || tableName === 'ChangeOfSchedule')
    && (path === 'daterequested' || path === 'datefrom' || path === 'dateto')) {

    if (!value) return '-';

    const date = new Date(value);
    if (isNaN(date.getTime())) return '-';
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  // 4. UPDATED OVERTIME/DTR/LEAVE CONTENT TO USE IMAGEZOOM
  if ((tableName === 'Overtime'
    || tableName === 'DTRCorrection'
    || tableName === 'Leave'
    || tableName === 'ChangeOfSchedule')
    && (path === 'content')) {

    if (!value) {
      return <></>
    }

    const imageSrc = 'data:image/jpeg;base64,' + value;
    return <ImageZoom src={imageSrc} alt="Request Attachment" />;
  }

  if (
    (tableName === 'Overtime' ||
      tableName === 'DTRCorrection' ||
      tableName === 'Leave' ||
      tableName === 'ChangeOfSchedule') &&
    path === 'approvals'
  ) {
    if (!value) return <>-</>;

    const getStatusClass = (status) => {
      switch (status) {
        case 'Approved':
          return 'text-green-500';
        case 'Disapproved':
          return 'text-red-500';
        case 'Cancelled':
          return 'text-gray-400'; // faded gray
        default:
          return ''; // Sent or undefined — normal text
      }
    };

    return (
      <>
        {value.primaryapproverstatus !== undefined && (
          <div className="mb-2 flex flex-col">
            Primary Approver Status:{' '}
            <span className={getStatusClass(value.primaryapproverstatus)}>
              {value.primaryapproverstatus ?? 'Sent'}
            </span>
            {(value.primaryapproverstatus === "Approved"
              || value.primaryapproverstatus === "Disapproved"
              || value.primaryapproverstatus === "Cancelled") &&
              <>
                <span>
                  {`${value.primaryapprover?.fullname}`}
                </span>
                <span>
                  {`${formatDate(value.primaryapproverupdatedate)}`}
                </span>
              </>
            }
          </div>
        )}
        {value.secondaryapproverstatus !== undefined && (
          <div className="mb-2 flex flex-col">
            Secondary Approver Status:{' '}
            <span className={getStatusClass(value.secondaryapproverstatus)}>
              {value.secondaryapproverstatus ?? 'Sent'}
            </span>
            {(value.secondaryapproverstatus === "Approved"
              || value.secondaryapproverstatus === "Disapproved"
              || value.secondaryapproverstatus === "Cancelled") &&
              <>
                <span>
                  {`${value.secondaryapprover?.fullname}`}
                </span>
                <span>
                  {`${formatDate(value.secondaryapproverupdatedate)}`}
                </span>
              </>
            }
          </div>
        )}
        {value.tertiaryapproverstatus !== undefined && (
          <div className="flex flex-col">
            Tertiary Approver Status:{' '}
            <span className={getStatusClass(value.tertiaryapproverstatus)}>
              {value.tertiaryapproverstatus ?? 'Sent'}
            </span>
            {(value.tertiaryapproverstatus === "Approved"
              || value.tertiaryapproverstatus === "Disapproved"
              || value.tertiaryapproverstatus === "Cancelled") &&
              <>
                <span>
                  {`${value.tertiaryapprover?.fullname}`}
                </span>
                <span>
                  {`${formatDate(value.tertiaryapproverupdatedate)}`}
                </span>
              </>
            }
          </div>
        )}
      </>
    );
  }

  if (tableName === 'Overtime' && (path === 'hasovertimeallowance' || path === 'isabovetwohoursovertime')) {
    return value ? 'True' : 'False';
  }

  if (tableName === 'Overtime' && (path === 'referencenumber')) {
    if (!value) return '-';
    return `OT-${value}`;
  }

  if (tableName === 'DTRCorrection' && (path === 'timein' || path === 'timeout' || path === 'timerendered')) {
    if (!value) {
      return <>-</>
    }

    const date = new Date(value);
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    const _date = `${mm}/${dd}/${yyyy}`;

    const _time = date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    return `${_date} ${_time}`
  }

  if (tableName === 'DTRCorrection' && (path === 'referencenumber')) {
    if (!value) return '-';
    return `DC-${value}`;
  }

  if (tableName === 'Leave' && (path === 'referencenumber')) {
    if (!value) return '-';
    return `LV-${value}`;
  }


  if (tableName === 'Leave' && path === 'ishalfday') {
    return value ? 'True' : 'False';
  }

  if (tableName === 'ChangeOfSchedule' && (path === 'referencenumber')) {
    if (!value) return '-';
    return `CS-${value}`;
  }

  if (tableName === 'ApproverHierarchy' && path === 'primaryapprovers') {
    if (!value) return <>-</>;

    return (
      <>
        {value.primaryapprover1?.fullname && <div className="mb-2">{value.primaryapprover1.fullname}</div>}
        {value.primaryapprover2?.fullname && <div className="mb-2">{value.primaryapprover2.fullname}</div>}
        {value.primaryapprover3?.fullname && <div className="mb-2">{value.primaryapprover3.fullname}</div>}
        {value.primaryapprover4?.fullname && <div>{value.primaryapprover4.fullname}</div>}
      </>
    );
  }

  if (tableName === 'ApproverHierarchy' && path === 'secondaryapprovers') {
    if (!value) return <>-</>;

    return (
      <>
        {value.secondaryapprover1?.fullname && <div className="mb-2">{value.secondaryapprover1.fullname}</div>}
        {value.secondaryapprover2?.fullname && <div className="mb-2">{value.secondaryapprover2.fullname}</div>}
        {value.secondaryapprover3?.fullname && <div className="mb-2">{value.secondaryapprover3.fullname}</div>}
        {value.secondaryapprover4?.fullname && <div>{value.secondaryapprover4.fullname}</div>}
      </>
    );
  }

  if (tableName === 'ApproverHierarchy' && path === 'tertiaryapprovers') {
    if (!value) return <>-</>;

    return (
      <>
        {value.tertiaryapprover1?.fullname && <div className="mb-2">{value.tertiaryapprover1.fullname}</div>}
        {value.tertiaryapprover2?.fullname && <div className="mb-2">{value.tertiaryapprover2.fullname}</div>}
        {value.tertiaryapprover3?.fullname && <div className="mb-2">{value.tertiaryapprover3.fullname}</div>}
        {value.tertiaryapprover4?.fullname && <div>{value.tertiaryapprover4.fullname}</div>}
      </>
    );
  }

  // Dev Mode
  if (tableName === "MonthlyWorkingDay" && (path === 'dropdownTypeName' || path === 'dropdownValueName')) {
    return (value === 0 || value === "0") ? 'All' : value;
  }

  if (tableName === "OTFormulas") {

    if (path === 'outputValueTypeName') return (value === null || value === "") ? '-' : value;
    if (path === 'outputValue') return value === null ? '-' : value;

    return value;
  }


  if (tableName === "GovernmentPremiumFormula") {

    if (path === 'hasSSS' || path === 'hasPhilhealth' || path === 'hasPagibig') {
      return value ? 'True' : 'False';
    }
  }

  return value;
};

// 5. ENHANCED CreateTableNew with custom cell rendering support
export const CreateTableNew = ({
  tableName,
  headers,
  accessors,
  data = [],
  emptyMessage = "No data available",
  actions = [],
  currentPage,
  setCurrentPage,
  itemsPerPage,
  totalItems,
  customCellRenderer = null, // New prop for custom rendering
}) => {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });

  const allowSort = true;

  /* ============================
     Sorting
  ============================ */
  const handleSort = (accessor) => {
    if (!allowSort || accessor === "actions") return;

    setSortConfig((prev) => {
      if (prev.key === accessor) {
        if (prev.direction === null) {
          return { key: accessor, direction: "asc" };
        }
        if (prev.direction === "asc") {
          return { key: accessor, direction: "desc" };
        }
        return { key: null, direction: null };
      }

      return { key: accessor, direction: "asc" };
    });
  };

  const getSortSymbol = (accessor) => {
    if (sortConfig.key !== accessor) return "";
    if (sortConfig.direction === "asc") return "▲";
    if (sortConfig.direction === "desc") return "▼";
    return "";
  };

  /* ============================
     Sorted Data
  ============================ */
  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return data;

    return [...data].sort((a, b) => {
      const aValue = getNestedValue(tableName, a, sortConfig.key, actions);
      const bValue = getNestedValue(tableName, b, sortConfig.key, actions);

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [data, sortConfig, tableName, actions]);

  /* ============================
     Pagination
  ============================ */
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  /* Reset to page 1 when sorting */
  useEffect(() => {
    setCurrentPage(1);
  }, [sortConfig, setCurrentPage]);

  /* ============================
     Render
  ============================ */
  return (
    <div className="w-full overflow-x-auto">
      <table
        className="min-w-full table-auto border border-gray-300 text-left text-sm"
        style={{ border: "1px solid #dfdfdf" }}
      >
        <thead className="bg-[#668fca] font-semibold uppercase tracking-wider text-white">
          <tr>
            {headers.map((header, index) => {
              const accessor = accessors[index];

              return (
                <th
                  key={index}
                  onClick={() => handleSort(accessor)}
                  className={`px-4 py-2 select-none ${allowSort && accessor !== "actions" ? "cursor-pointer" : ""
                    }`}
                >
                  <div className="flex min-w-[100px] items-center gap-1">
                    {header}
                    <span className="text-xs">{getSortSymbol(accessor)}</span>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {paginatedData.length > 0 ? (
            paginatedData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="border-t odd:bg-white even:bg-gray-50 hover:bg-gray-100"
              >
                {accessors.map((accessor, colIndex) => (
                  <td
                    key={colIndex}
                    // MODIFIED: Changed max-w-[150px] to min-w-[120px] and removed whitespace-nowrap 
                    // only if it's the actions column or if it's meant to be flexible.
                    className={`px-4 py-2 ${accessor !== 'actions' ? 'min-w-[120px]' : ''} ${accessor === 'errors' ? 'whitespace-normal' : 'max-w-[200px] overflow-hidden whitespace-nowrap'
                      }`}
                    title={typeof getNestedValue(tableName, row, accessor, actions) === 'string'
                      ? getNestedValue(tableName, row, accessor, actions)
                      : undefined}  // shows full text on native hover tooltip
                  >
                    {accessor === "actions" ? (
                      <div className="flex space-x-1">
                        {actions.map((action, actionIndex) => {
                          // 1. Determine Visibility
                          let isVisible = action.visible;

                          // If it's a function, run it against the current row
                          if (typeof action.visible === "function") {
                            isVisible = action.visible(row);
                          }

                          // 2. Backward Compatibility Check
                          if (!isVisible) return null;

                          let isDisabled = action.disabled;
                          if (typeof action.disabled === "function") {
                            isDisabled = action.disabled(row);
                          }


                          return (
                            <button
                              key={actionIndex}
                              onClick={(e) => {
                                console.log({ isDisabled });
                                if (isDisabled) return;
                                action.function(row);
                              }}
                              className={`text-white ${action.color || ''} p-[1px] rounded-md border-none max-h-6 ${action.className} ${isDisabled
                                ? "opacity-40 cursor-not-allowed filter grayscale"
                                : "cursor-pointer hover:scale-110 transition-transform"
                                }`}
                              title={isDisabled ? "Action Not Available" : action.tooltip}
                            >
                              {action.icon}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      (() => {
                        // 1. Try to get content from custom renderer first
                        const customContent = customCellRenderer ? customCellRenderer(row, accessor) : null;

                        // 2. If custom renderer returned something, show it
                        if (customContent !== null) return customContent;

                        // 3. Otherwise, fall back to default getNestedValue logic
                        const value = getNestedValue(tableName, row, accessor, actions) ?? "-";
                        const isTrue = ["True", "Yes", "Subscribed", "Active", "Approved"].includes(value);
                        const isFalse = ["False", "No", "Unsubscribed", "Inactive", "Unapproved"].includes(value);

                        return (
                          <span className={`${isTrue && accessor !== "description" ? "text-green-500 font-semibold" : ""} 
                        ${isFalse && accessor !== "description" ? "text-red-500 font-semibold" : ""}`}>
                            {value}
                          </span>
                        );
                      })()
                    )}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={headers.length}
                className="px-4 py-4 text-center text-gray-500"
              >
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        allowFirstAndLastPage
        onPageChange={(page) => {
          if (page < 1 || page > totalPages) return;
          setCurrentPage(page);
        }}
      />
    </div>
  );
};
// Add this new component to your common components file (or create a new one)
export const CreateTableWithSelection = ({
  tableName,
  headers,
  accessors,
  data = [],
  emptyMessage = "No data available",
  selectedItems = [],
  onSelectItem,
  onSelectAll,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  totalItems,
  selectionDisabledChecker = null, // Function to check if item can be selected
  selectionDisabledMessage = "Already selected" // Message for disabled items
}) => {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });

  const allowSort = true;

  /* ============================
     Sorting
  ============================ */
  const handleSort = (accessor) => {
    if (!allowSort || accessor === "selection" || accessor === "actions") return;

    setSortConfig((prev) => {
      if (prev.key === accessor) {
        if (prev.direction === null) {
          return { key: accessor, direction: "asc" };
        }
        if (prev.direction === "asc") {
          return { key: accessor, direction: "desc" };
        }
        return { key: null, direction: null };
      }

      return { key: accessor, direction: "asc" };
    });
  };

  const getSortSymbol = (accessor) => {
    if (sortConfig.key !== accessor) return "";
    if (sortConfig.direction === "asc") return "▲";
    if (sortConfig.direction === "desc") return "▼";
    return "";
  };

  /* ============================
     Sorted Data
  ============================ */
  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return data;

    return [...data].sort((a, b) => {
      const aValue = getNestedValue(tableName, a, sortConfig.key, []);
      const bValue = getNestedValue(tableName, b, sortConfig.key, []);

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [data, sortConfig, tableName]);

  /* ============================
     Pagination
  ============================ */
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  /* Reset to page 1 when sorting */
  useEffect(() => {
    setCurrentPage(1);
  }, [sortConfig, setCurrentPage]);

  /* ============================
     Selection Logic
  ============================ */
  const isAllSelected = useMemo(() => {
    if (paginatedData.length === 0) return false;

    // Filter out disabled items for "select all" calculation
    const selectableItems = paginatedData.filter(item =>
      !selectionDisabledChecker || !selectionDisabledChecker(item)
    );

    if (selectableItems.length === 0) return false;

    return selectableItems.every(item =>
      selectedItems.some(selected => selected.id === item.id)
    );
  }, [paginatedData, selectedItems, selectionDisabledChecker]);

  const handleToggleAll = () => {
    if (isAllSelected) {
      // Deselect all selectable items on current page
      const selectableIds = paginatedData
        .filter(item => !selectionDisabledChecker || !selectionDisabledChecker(item))
        .map(item => item.id);

      onSelectAll?.(selectedItems.filter(item => !selectableIds.includes(item.id)));
    } else {
      // Select all selectable items on current page
      const itemsToSelect = paginatedData.filter(item =>
        !selectionDisabledChecker || !selectionDisabledChecker(item)
      );

      // Combine with already selected items (avoid duplicates)
      const newSelection = [...selectedItems];
      itemsToSelect.forEach(item => {
        if (!newSelection.some(selected => selected.id === item.id)) {
          newSelection.push(item);
        }
      });

      onSelectAll?.(newSelection);
    }
  };

  const isItemSelected = (item) => {
    return selectedItems.some(selected => selected.id === item.id);
  };

  const isItemDisabled = (item) => {
    return selectionDisabledChecker?.(item) || false;
  };

  /* ============================
     Render
  ============================ */
  return (
    <div className="w-full overflow-x-auto">
      <table
        className="min-w-full table-auto border border-gray-300 text-left text-sm"
        style={{ border: "1px solid #dfdfdf" }}
      >
        <thead className="bg-[#668fca] font-semibold uppercase tracking-wider text-white">
          <tr>
            {/* Selection checkbox header */}
            <th className="px-4 py-2 w-12">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={handleToggleAll}
                disabled={paginatedData.length === 0}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 cursor-pointer"
              />
            </th>

            {/* Other headers */}
            {headers.map((header, index) => {
              const accessor = accessors[index];
              if (accessor === "selection") return null; // Skip selection column header

              return (
                <th
                  key={index}
                  onClick={() => handleSort(accessor)}
                  className={`px-4 py-2 select-none ${allowSort && accessor !== "actions" ? "cursor-pointer" : ""
                    }`}
                >
                  <div className="flex min-w-[100px] items-center gap-1">
                    {header}
                    <span className="text-xs">{getSortSymbol(accessor)}</span>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {paginatedData.length > 0 ? (
            paginatedData.map((row, rowIndex) => {
              const isSelected = isItemSelected(row);
              const isDisabled = isItemDisabled(row);

              return (
                <tr
                  key={rowIndex}
                  className={`border-t odd:bg-white even:bg-gray-50 hover:bg-gray-100 ${isDisabled ? "opacity-50" : ""
                    }`}
                >
                  {/* Selection checkbox cell */}
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => !isDisabled && onSelectItem?.(row)}
                      disabled={isDisabled}
                      className={`h-4 w-4 rounded border-gray-300 text-blue-600 ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"
                        }`}
                    />
                  </td>

                  {/* Other cells */}
                  {accessors.map((accessor, colIndex) => {
                    if (accessor === "selection") return null;

                    return (
                      <td
                        key={colIndex}
                        className="max-w-[150px] overflow-hidden whitespace-nowrap px-4 py-2 hover:overflow-auto"
                      >
                        {accessor === "actions" ? (
                          <div className="flex space-x-2">
                            {/* Actions if needed */}
                          </div>
                        ) : (
                          (() => {
                            const value = getNestedValue(tableName, row, accessor, []) ?? "-";

                            // Add indicator for disabled items
                            if (isDisabled && accessor === "fullName" && selectionDisabledMessage) {
                              return (
                                <div className="flex flex-col">
                                  <span>{value}</span>
                                  <span className="text-xs text-green-600 mt-1">
                                    ✓ {selectionDisabledMessage}
                                  </span>
                                </div>
                              );
                            }

                            const isTrue = ["True", "Yes", "Subscribed", "Active", "Approved"].includes(value);
                            const isFalse = ["False", "No", "Unsubscribed", "Inactive", "Unapproved"].includes(value);

                            return (
                              <span
                                className={`${isTrue && accessor !== "description"
                                  ? "text-green-500 font-semibold"
                                  : ""
                                  } ${isFalse && accessor !== "description"
                                    ? "text-red-500 font-semibold"
                                    : ""
                                  }`}
                              >
                                {value}
                              </span>
                            );
                          })()
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={headers.length + 1} // +1 for selection column
                className="px-4 py-4 text-center text-gray-500"
              >
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        allowFirstAndLastPage
        onPageChange={(page) => {
          if (page < 1 || page > totalPages) return;
          setCurrentPage(page);
        }}
      />
    </div>
  );
};

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  allowFirstAndLastPage = true,
}) => {
  if (totalPages <= 1) return null;

  return (
    <div className="mb-3 mt-4 flex items-center justify-center space-x-4">

      {allowFirstAndLastPage && (
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="rounded border-none bg-gray-200 px-3 py-1 disabled:opacity-50"
        >
          <FirstPageIcon className="text-gray-500" fontSize="small" />
        </button>
      )}

      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="rounded border-none bg-gray-200 px-3 py-1 disabled:opacity-50"
      >
        <NavigateBeforeIcon className="text-gray-600" fontSize="small" />
      </button>

      <span className="text-gray-600">
        Page {currentPage} of {totalPages}
      </span>

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="rounded border-none bg-gray-200 px-3 py-1 disabled:opacity-50"
      >
        <NavigateNextIcon className="text-gray-600" fontSize="small" />
      </button>

      {allowFirstAndLastPage && (
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="rounded border-none bg-gray-200 px-3 py-1 disabled:opacity-50"
        >
          <LastPageIcon className="text-gray-600" fontSize="small" />
        </button>
      )}
    </div>
  );
};



export const CreateTable = ({
  tableName,
  headers,
  accessors,
  data,
  emptyMessage = 'No data available',
  actions,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  totalItems
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // Calculate paginated data internally
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = data?.slice(startIndex, endIndex) || [];

  const rowsPerPage = itemsPerPage;
  const totalPages = Math.ceil(totalItems / rowsPerPage);

  const allowSort = false;

  const handleSort = (accessor) => {
    if (allowSort) {
      setSortConfig(prevConfig => {
        if (prevConfig.key === accessor) {
          // Cycle through: null -> ascending -> descending -> null ...
          if (prevConfig.direction === null) {
            return { key: accessor, direction: 'asc' };
          } else if (prevConfig.direction === 'asc') {
            return { key: accessor, direction: 'desc' };
          } else {
            return { key: null, direction: null };
          }
        } else {
          return { key: accessor, direction: 'asc' };
        }
      });
    }
  };

  const getSortSymbol = (accessor) => {
    if (sortConfig.key !== accessor) return '';
    if (sortConfig.direction === 'asc') return '▲';
    if (sortConfig.direction === 'desc') return '▼';
    return '';
  };

  return (
    <>
      <div className="w-full overflow-x-auto">
        <table className="min-w-full table-auto border border-gray-300 text-left text-sm" style={{ border: "1px solid #dfdfdf" }}>
          <thead className="bg-[#668fca] font-semibold uppercase tracking-wider text-white">
            <tr>
              {headers.map((header, index) => (
                <th
                  key={index}
                  onClick={() => handleSort(accessors[index])}
                  className={`px-4 py-2 ${allowSort ? "cursor-pointer select-none" : ""}`}
                >
                  <div className="flex min-w-[100px] items-center gap-1">
                    {header}
                    <span className="text-xs">{getSortSymbol(accessors[index])}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData?.length > 0 ? ( // Use paginatedData instead of data
              paginatedData.map((row, rowIndex) => ( // Use paginatedData here too
                <tr key={rowIndex} className="border-t odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                  {accessors.map((accessor, colIndex) => (
                    <td key={colIndex}
                      className={`px-4 py-2 max-w-[150px] overflow-hidden hover:overflow-auto whitespace-nowrap
                    `}
                    >
                      {accessor === 'actions' ? (
                        <div className="flex space-x-2">
                          {actions.filter(i => i.visible).map((action, actionIndex) => (
                            <button
                              key={actionIndex}
                              onClick={() => action.function(row)}
                              className={`text-white ${action.color} p-[1px] rounded-md ${action.className} border-none cursor-pointer flex- items-center`}
                              title={action.tooltip}
                            >
                              {action.icon}
                            </button>
                          ))}
                        </div>
                      ) : (
                        (() => {
                          const value = getNestedValue(tableName, row, accessor, actions) ?? '-';
                          const isTrue = value === 'True' || value === "Yes" || value === "Subscribed" || value === "Active" || value === "Approved";
                          const isFalse = value === 'False' || value === "No" || value === "Unsubscribed" || value === "Inactive" || value === "Unapproved";
                          return (
                            <span className={`${isTrue && accessor !== "description" ? 'text-green-500 font-semibold' : ''} ${isFalse && accessor !== "description" ? 'text-red-500 font-semibold' : ''}
                                        ${accessor === "activeUsers" ? "text-green-500 font-semibold" : ""}
                                        ${accessor === "inActiveUsers" ? "text-red-500 font-semibold" : ""}
                            `}>
                              {value}
                            </span>
                          );
                        })()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={headers.length} className="px-4 py-4 text-center text-gray-500">
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination Controls */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          allowFirstAndLastPage={true}
          onPageChange={(page) => {
            if (page < 1 || page > totalPages) return;
            setCurrentPage(page);
          }}
        />
      </div>
    </>
  );
};
//#endregion













//#region Button
export const Button = ({
  id = "",
  icon,
  name,
  className = "",
  onClick,
  title = "",       // optional, default empty string
  disabled = false, // optional, default false
  type = "button"   // optional, default "button"
}) => {
  return (
    <button
      id={id}
      type={type}
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={cn(
        "flex items-center px-4 py-2 text-white rounded-md border-0 shadow gap-2 w-max transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer",
        className
      )}
    >
      {icon}
      <span>{name}</span>
    </button>
  );
};
export const ButtonIcon = ({ icon, name, className, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`flex items-center px-3 py-1 text-white rounded-lg shadow w-max ${className}`}
    >
      {icon}
    </button>
  );
};
export const SmallButton = ({ icon, name, className, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`flex items-center px-2 py-1 text-sm text-white rounded-md shadow gap-2 w-max ${className}`}
    >
      {icon}
      <span>{name}</span>
    </button>
  );
};
//#endregion













//#region Message Modal
export const SuccessMessage = ({ message, onClose }) => {
  if (!message) return null;

  return (
    <div className="fixed left-1/2 top-5 z-[10001] max-w-sm -translate-x-1/2 md:max-w-md">

      {/* Container: Modern styling */}
      <div
        // Use 'items-center' for vertical alignment of the icon and text line
        className="flex items-center space-x-3 rounded-lg border-l-4 border-green-600 bg-green-200 p-4 shadow-xl"
        role="alert"
      >
        {/* Error Icon Section */}
        {/* We use flex-shrink-0 to prevent the icon from squeezing */}
        <div className="flex-shrink-0">
          <ErrorOutlineIcon className="h-6 w-6 text-green-600" />
        </div>

        {/* Message Content Section (Restored to original structure) */}
        <div className="flex-grow text-sm">
          {/* Aligned original content: Strong and Span */}
          <strong className="font-bold text-green-800">Success! </strong>
          <span className="text-green-700">{message}</span>
        </div>

        {/* Close Button Section */}
        <button
          onClick={onClose}
          className="border-none bg-transparent text-green-500 transition-colors hover:text-green-700"
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>
    </div>
  );
};
export const FailMessage = ({ message, onClose }) => {
  if (!message) return null;

  return (
    // Fixed position, centered, high z-index
    <div className="fixed left-1/2 top-5 z-[10001] max-w-sm -translate-x-1/2 md:max-w-md">

      {/* Container: Modern styling */}
      <div
        // Use 'items-center' for vertical alignment of the icon and text line
        className="flex items-center space-x-3 rounded-lg border-l-4 border-red-600 bg-red-200 p-4 shadow-xl"
        role="alert"
      >
        {/* Error Icon Section */}
        {/* We use flex-shrink-0 to prevent the icon from squeezing */}
        <div className="flex-shrink-0">
          <ErrorOutlineIcon className="h-6 w-6 text-red-600" />
        </div>

        {/* Message Content Section (Restored to original structure) */}
        <div className="flex-grow text-sm">
          {/* Aligned original content: Strong and Span */}
          <strong className="font-bold text-red-800">Error! </strong>
          <span className="text-red-700">{message}</span>
        </div>

        {/* Close Button Section */}
        <button
          onClick={onClose}
          className="border-none bg-transparent text-red-500 transition-colors hover:text-red-700"
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>
    </div>
  );
};
export const DeclinedMessage = ({ message, onClose }) => {
  if (!message) return null;

  return (
    <div className="fixed left-1/2 top-5 z-[10001] max-w-sm -translate-x-1/2 md:max-w-md">

      <div
        className="flex items-center space-x-3 rounded-lg border-l-4 border-orange-500 bg-orange-100 p-4 shadow-xl"
        role="alert"
      >
        {/* Icon */}
        <div className="flex-shrink-0">
          <SentimentDissatisfiedIcon className="h-6 w-6 text-orange-600" />
        </div>

        {/* Message */}
        <div className="flex-grow text-sm">
          <strong className="font-bold text-orange-800">Notice: </strong>
          <span className="text-orange-700">{message}</span>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="border-none bg-transparent text-orange-500 transition-colors hover:text-orange-700"
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>
    </div>
  );
};
export const ConfirmationModal = ({ visible, onClose, onSave, modalName, message }) => {
  if (!visible) return null;

  const handleSubmit = async () => {
    await onSave();
    onClose();
  };

  return (
    <div className="fixed inset-0 mx-auto flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 9999 }}>
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
        <h2 className="mb-4 text-2xl font-semibold">{modalName}</h2>

        <div className="short:max-h-[300px] medium:h-[400px] mt-10 max-h-[500px] space-y-5 overflow-y-auto">
          <p className="text-base text-gray-700">{message}</p>
        </div>

        <div className="mt-6 flex justify-end space-x-2">
          <button
            className="rounded-md bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700"
            onClick={handleSubmit}
          >
            Yes
          </button>
          <button
            className="rounded-md bg-red-600 px-4 py-2 text-white transition hover:bg-red-700"
            onClick={onClose}
          >
            No
          </button>
        </div>
      </div>
    </div>
  );
};

export const GeneralMessage = ({
  form,           // boolean to show/hide
  message,        // message content
  isSuccess,      // boolean for success/error styling
  duration = 3000 // shorter duration
}) => {
  const [internalShow, setInternalShow] = useState(form);
  const [key, setKey] = useState(0); // Add a key to force re-render

  // Reset component when form changes
  useEffect(() => {
    if (form) {
      // Force re-render by changing key
      setKey(prev => prev + 1);
      setInternalShow(true);

      const timer = setTimeout(() => {
        setInternalShow(false);
      }, duration);

      return () => clearTimeout(timer);
    } else {
      setInternalShow(false);
    }
  }, [form, message, isSuccess, duration]); // Add all dependencies

  if (!internalShow) return null;

  return (
    <div key={key} className={`modal-overlay toggleMessage-overlay z-[10002] ${internalShow ? 'open' : ''}`}>
      <div className="modal toggleMessage" style={{ backgroundColor: isSuccess ? "#E0FFDC" : "#ffd0c5" }}>
        <div className='column'>
          {isSuccess ?
            <div className='request_title row' style={{ fontWeight: "bold", color: "green", alignItems: "center" }}>
              <CheckCircleIcon />
              <span>SUCCESS MESSAGE</span>
            </div>
            :
            <div className='request_title row' style={{ fontWeight: "bold", color: "#DD5746", alignItems: "center" }}>
              <ErrorIcon />
              <span>ERROR MESSAGE</span>
            </div>
          }
          <span style={{ textAlign: "center", padding: "10px 0", color: isSuccess ? "green" : "#DD5746" }}>
            {message}
          </span>
        </div>
      </div>
    </div>
  );
};

export const useGeneralMessage = (defaultDuration = 3000) => {
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(true);
  const [duration, setDuration] = useState(defaultDuration);
  const [messageKey, setMessageKey] = useState(0);

  // ✅ Memoize the function so identity doesn't change
  const showGeneralMessage = useCallback(
    (msg, success = true, customDuration = defaultDuration) => {
      setShowMessage(false); // hide first

      setTimeout(() => {
        setMessage(msg);
        setIsSuccess(success);
        setDuration(customDuration);
        setMessageKey(prev => prev + 1);
        setShowMessage(true); // show again
      }, 10);
    },
    [defaultDuration] // only changes if defaultDuration changes
  );

  const hideGeneralMessage = useCallback(() => {
    setShowMessage(false);
  }, []);

  return {
    showMessage,
    message,
    isSuccess,
    duration,
    messageKey,
    showGeneralMessage,
    hideGeneralMessage,
  };
};
//#endregion















export function OnBoardingPersonnelSelectForm({ isOpen,
  onClose,
  data,
  setData }) {
  const [failMessage, setFailMessage] = useState("");

  var header = [
    "Id",
    "Name",
  ]
  var accessor = [
    "employeeId",
    "fullName"
  ]

  const handleClose = () => {
    onClose();
    fetchData();
    setFailMessage("")
  };
  const handleProceed = () => {
    if (!selectedEmployee) {
      setFailMessage("Please select employee first.")
      return;
    }

    onClose();
    setData(selectedEmployee);
    fetchData();
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [filterData, setFilterData] = useState([]);
  const [filterDataIndex, setFilterDataIndex] = useState(0);
  const [searchPlaceholder, setSearchPlaceholder] = useState("Search by name");

  const fetchData = async () => {
    const apiUrl = domain + "/api/OnBoardingPersonnel/active";

    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })
      setEmployees(response.data); // Set the response data to the state
      setFilterData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false); // Set loading state to false after data fetching is complete
    }
  }
  const handleFilter = (value) => {
    setCurrentPage(1);
    const lowercaseValue = value.toLowerCase();
    var res = [];

    switch (filterDataIndex) {
      case 0:
        res = filterData.filter((f) =>
          f.fullName.toLowerCase().includes(lowercaseValue)
        );
        break;
      case 1:
        res = filterData.filter((f) =>
          f.employeeId.toString().startsWith(lowercaseValue)
        );
        break;
      default:
        res = filterData.filter((f) =>
          f.fullName.toLowerCase().includes(lowercaseValue)
        );

        break;
    }

    setEmployees(res);
  };

  useEffect(() => {
    switch (filterDataIndex) {
      case 0:
        setSearchPlaceholder("Search by name")
        break;
      case 1:
        setSearchPlaceholder("Search by id")
        break;
      default:
        setSearchPlaceholder("Search by name")
        break;
    }
    fetchData();
  }, [filterDataIndex]);

  return (
    <>
      <div className={`modal-overlay ${isOpen ? 'open' : ''}`}>
        {isOpen && (
          <div className="modal">
            <div className="popUp-header row">
              <div className="popUp-title">
                <h3 style={{ color: "#668fca" }}>Select Employee</h3>
              </div>
              <div className="popUp-close">
                <CloseIcon onClick={() => handleClose()} />
              </div>
            </div>
            <div className="transaction-body">
              <OnBoardingPersonnelFilter setFilterDataIndex={setFilterDataIndex} />
              <div className="transaction-body-top row">
                {CreateSearchBarWithPlaceHolder("",
                  "transaction-search row",
                  "transaction-search-icon",
                  "transaction-search-input",
                  (e) => handleFilter(e.target.value),
                  searchPlaceholder,
                  SearchIcon)}
              </div>
            </div>
            <div className="popUp-body">
              <div className="select-employee-table">
                <CreateTableForEmployeeSelect
                  dataList={employees}
                  dataHeader={header}
                  dataAccessor={accessor}
                  setSelectedEmployee={setSelectedEmployee}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                />
                {employees.length > 0 ? (
                  <div></div>
                )
                  :
                  (
                    <div>
                      {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '10vh' }}>
                          <img src={loadingSpinner} alt="Loading..." />
                        </div>
                      ) : (
                        <div> {/*<p>No records to display</p>*/}</div>
                      )
                      }
                    </div>
                  )}
              </div>
            </div>
            <div className="buttons row" style={{ justifyContent: "end", padding: "50px 20px 0 20px" }}>
              <button id="add-family"
                className="save row"
                onClick={handleProceed}>
                PROCEED
              </button>
            </div>
          </div>
        )}
      </div>


      <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
    </>
  );
}
function OnBoardingPersonnelFilter({ setFilterDataIndex }) {
  const [selectedOption, setSelectedOption] = useState("All");

  const handleSelectChange = (event) => {
    setSelectedOption(event.target.value);

    switch (event.target.value) {
      case "All":
        setFilterDataIndex(0);
        break;
      case "EmployeeId":
        setFilterDataIndex(1);
        break;
      default:
        setFilterDataIndex(0);
        break;
    }
  };

  return (
    <div className="filters row" style={{ alignItems: "center" }}>
      <span style={{ marginRight: "10px" }}>Filter</span>
      <select id="" className="filterSelect" value={selectedOption} onChange={handleSelectChange}>
        <option value="All">All</option>
        <option value="EmployeeId">Employee Id</option>
      </select>
    </div>
  );
};

export function OnBoardingRequirementsSelect({ data, setData, isEditable, hasAll }) {
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    const foundData = _data.find(i => i.id.toString() === event.target.value.toString());
    setData(foundData);
  };

  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/OnBoardingRequirements/active";

    await axios.get(apiUrl, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json' // You can include other headers if needed
      }
    })
      .then(response => {
        _setData(response.data); // Set the response data to the state
      })
      .catch(error => {
        console.error('Error fetching data:', error);
      });
  }

  useEffect(() => {
    fetchData();
    setIsSelected(data)
  }, [data]);

  return (
    <div>
      <select value={data ? data.id : ""}
        className={`w-full rounded-md h-8 ${isSelected ? "text-gray-600" : "text-gray-400"}`}
        style={{ border: "1px solid #d7d7d7" }}
        onChange={handleSelectChange}
        disabled={!isEditable}
      >
        {hasAll ?
          <option value="">All</option>
          :
          <option value="" disabled hidden>Choose requirement</option>
        }
        {_data.map((dt, index) => (
          <option key={index} value={dt.id}>{
            dt.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function OutBoardingRequirementsSelect({ data, setData, isEditable, hasAll }) {
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    const foundData = _data.find(i => i.id.toString() === event.target.value.toString());
    setData(foundData);
  };

  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/OutBoardingRequirements/active";

    await axios.get(apiUrl, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json' // You can include other headers if needed
      }
    })
      .then(response => {
        _setData(response.data); // Set the response data to the state
      })
      .catch(error => {
        console.error('Error fetching data:', error);
      });
  }

  useEffect(() => {
    fetchData();
    setIsSelected(data)
  }, [data]);

  return (
    <div>
      <select value={data ? data.id : ""}
        className={`w-full rounded-md h-8 ${isSelected ? "text-gray-600" : "text-gray-400"}`}
        style={{ border: "1px solid #d7d7d7" }}
        onChange={handleSelectChange}
        disabled={!isEditable}
      >
        {hasAll ?
          <option value="">All</option>
          :
          <option value="" disabled hidden>Choose requirement</option>
        }
        {_data.map((dt, index) => (
          <option key={index} value={dt.id}>{
            dt.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function LicenseSelect({ data, setData, isEditable }) {
  const [licenses, setLicenses] = useState([]);
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    const loadLicenses = async () => {
      try {
        const result = await fetchAvailableSlot();

        // const allResult = await fetchAllSlot();
        setLicenses(result ?? []);
      } catch (error) {
        console.error("Failed to fetch available licenses", error);
      }
    };

    loadLicenses();
    setIsSelected(!!data);
  }, [data]);

  const hasNoData = licenses.length === 0;

  return (
    <select
      value={data || ""}
      className={`w-full rounded-md h-8 ${isSelected ? "text-gray-600" : "text-gray-400"
        }`}
      style={{ border: "1px solid #d7d7d7" }}
      onChange={handleSelectChange}
      disabled={!isEditable}
    >
      <option value="" hidden>
        Choose available license
      </option>

      {/* No data state */}
      {hasNoData && (
        <option value="" disabled>
          -- No Available License --
        </option>
      )}

      {/* Data state */}
      {!hasNoData &&
        licenses.map((license, index) => (
          <option key={index} value={license.licenseKey}>
            {license.userCode}
          </option>
        ))}
    </select>
  );
}
export function LicenseAllSelect({ data, setData, isEditable }) {
  const [licenses, setLicenses] = useState([]);
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    const loadLicenses = async () => {
      try {
        const result = await fetchAllSlot();

        setLicenses(result ?? []);
      } catch (error) {
        console.error("Failed to fetch available licenses", error);
      }
    };

    loadLicenses();
    setIsSelected(!!data);
  }, [data]);

  const hasNoData = licenses.length === 0;

  return (
    <select
      value={data || ""}
      className={`w-full rounded-md h-8 ${isSelected ? "text-gray-600" : "text-gray-400"
        }`}
      style={{ border: "1px solid #d7d7d7" }}
      onChange={handleSelectChange}
      disabled={!isEditable}
    >
      <option value="" hidden>
        Choose available license
      </option>

      {/* No data state */}
      {hasNoData && (
        <option value="" disabled>
          -- No Available License --
        </option>
      )}

      {/* Data state */}
      {!hasNoData &&
        licenses.map((license, index) => (
          <option key={index} value={license.licenseKey}>
            {license.userCode}
          </option>
        ))}
    </select>
  );
}

//#endregion













//#region Searchbar
export const SearchBar = ({
  value = "",
  placeholder = "",
  handleFilter,
  parentClassName = "",
  className = ""
}) => {
  return (
    <div
      className={cn(
        "my-2 flex w-full max-w-md items-center rounded-md",
        parentClassName
      )}
    >
      <input
        type="text"
        onChange={(e) => handleFilter(e.target.value)}
        className={`w-full rounded-md border border-gray-300 px-4 py-2 text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
        style={{ border: "1px solid #d7d7d7" }}
        placeholder={placeholder ? placeholder : "Search..."}
      />
    </div>
  );
};
//#endregion













//#region Filter
export const MAINTENACE_CLIENT = 1; // has
export const MAINTENACE_PAYTYPE = 2;
export const MAINTENACE_DEPARTMENT = 3; // has
export const MAINTENACE_SECTION = 4;
export const MAINTENACE_CLASSIFICATION = 5;
export const MAINTENACE_COMPANY = 6;
export const MAINTENACE_POSITION = 7;
export const MAINTENACE_EMPLOYEESTATUS = 8;
export const MAINTENACE_JOBTITLE = 9;
export const MAINTENACE_EMPLOYEEGROUP1 = 10;
export const MAINTENACE_EMPLOYEEGROUP2 = 11;
export const MAINTENACE_LOCATION = 12;
export const MAINTENACE_TIMEKEEPER = 13;
export const MAINTENACE_PLANTILYA = 14;
export const MAINTENACE_COSTCENTER = 15;
export const MAINTENACE_VESSEL = 16;
export const MAINTENACE_EMPLOYEETYPE = 17; // has

// use this as reference for creating filter dropdown
export const LocationIdSelectWithAll = ({
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  value,
  onChange,
  setValue,
  placeholder = "Select location",
  className = "",
  parentClassName = "",
  error = false,
  errorMessage = "",
  isLoading = false,
  allowAll = false,
  ...props
}) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);

  const disabled = isEditMode || isDisabled || loading || isLoading;
  const displayValue = value || "0";
  const isSelected = displayValue && displayValue !== "0";

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          domain + "/api/Location/active",
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const sortedData = response.data.sort((a, b) =>
          a.description.localeCompare(b.description)
        );

        setLocations(sortedData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    if (onChange) {
      onChange(e);
    } else if (setValue) {
      setValue(e.target.value);
    }
  };

  const selectClasses = `
    w-full 
    border 
    ${error ? 'border-red-500' : 'border-gray-300'} 
    rounded-md 
    px-3 
    py-2 
    ${!isSelected ? 'text-gray-400' : 'text-gray-700'} 
    focus:outline-none 
    focus:ring-2 
    focus:ring-blue-500 
    focus:border-blue-500 
    bg-white 
    ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-70' : 'hover:border-gray-400'} 
    transition-colors 
    duration-150
    ${className}
  `.replace(/\s+/g, ' ').trim();

  return (
    <div className={`mb-1 ${parentClassName}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-500 mb-1.5">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <select
          value={displayValue}
          onChange={handleChange}
          disabled={disabled}
          className={selectClasses}
          {...props}
        >
          {loading ? (
            <option value="" disabled className="text-gray-400">
              Loading locations...
            </option>
          ) : (
            <>
              {!allowAll && (
                <option value="0" disabled hidden>
                  {placeholder}
                </option>
              )}

              {allowAll && (
                <option value="0">
                  All
                </option>
              )}

              {locations.map((location) => (
                <option
                  key={location.id}
                  value={location.id}
                  className="text-gray-700"
                >
                  {location.description}
                </option>
              ))}
            </>
          )}
        </select>

        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
          </div>
        )}
      </div>

      {error && errorMessage && (
        <span className="mt-1 block text-sm text-red-600">{errorMessage}</span>
      )}
    </div>
  );
};

export const TimeKeeperSelectWithAll = ({
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  value,
  onChange,
  setValue,
  placeholder = "Select time keeper",
  className = "",
  parentClassName = "",
  error = false,
  errorMessage = "",
  isLoading = false,
  ...props
}) => {
  const [timeKeepers, setTimeKeepers] = useState([]);
  const [loading, setLoading] = useState(false);

  const disabled = isEditMode || isDisabled || loading || isLoading;
  const displayValue = value || "0";
  const isSelected = displayValue && displayValue !== "0";

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          domain + "/api/Employee/all-timekeepers",
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const sortedData = response.data.sort((a, b) =>
          a.fullName.localeCompare(b.fullName)
        );

        setTimeKeepers(sortedData);
      } catch (error) {
        console.error("Error fetching timekeepers:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    if (onChange) {
      onChange(e);
    } else if (setValue) {
      setValue(e.target.value);
    }
  };

  const selectClasses = `
    w-full 
    border 
    ${error ? 'border-red-500' : 'border-gray-300'} 
    rounded-md 
    px-3 
    py-2 
    ${!isSelected ? 'text-gray-400' : 'text-gray-700'} 
    focus:outline-none 
    focus:ring-2 
    focus:ring-blue-500 
    focus:border-blue-500 
    bg-white 
    ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-70' : 'hover:border-gray-400'} 
    transition-colors 
    duration-150
    ${className}
  `.replace(/\s+/g, ' ').trim();

  return (
    <div className={`mb-1 ${parentClassName}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-500 mb-1.5">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <select
          value={displayValue}
          onChange={handleChange}
          disabled={disabled}
          className={selectClasses}
          {...props}
        >
          {loading ? (
            <option value="" disabled className="text-gray-400">
              Loading time keepers...
            </option>
          ) : (
            <>
              <option value="0" className="text-gray-700">All</option>
              {timeKeepers.map((tk) => (
                <option
                  key={tk.id}
                  value={tk.id}
                  className="text-gray-700"
                >
                  {tk.fullName}
                </option>
              ))}
            </>
          )}
        </select>

        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
          </div>
        )}
      </div>

      {error && errorMessage && (
        <span className="mt-1 block text-sm text-red-600">{errorMessage}</span>
      )}
    </div>
  );
};

export const ClientIdSelectWithAll = ({
  // New props structure
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  value,
  onChange,
  setValue,
  placeholder = "Select client",
  className = "",
  parentClassName = "",
  error = false,
  errorMessage = "",
  isLoading = false,

  // Backward compatibility props
  data,           // For old usage: selected client object
  setData,        // For old usage: function to set selected client object
  isEditable,     // For old usage: controls editability

  ...props
}) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  // Handle backward compatibility
  const isLegacyMode = data !== undefined || setData !== undefined;

  // Determine if disabled (support both new and legacy props)
  const disabled = isEditMode || isDisabled || loading || isLoading ||
    (isLegacyMode && !isEditable);

  // Determine value (support both new and legacy props)
  let displayValue;
  if (isLegacyMode) {
    displayValue = data?.id?.toString() || "0";
  } else {
    displayValue = value || "0";
  }

  const isSelected = displayValue && displayValue !== "0";

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          domain + "/api/Client/active",
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const sortedData = response.data.sort((a, b) =>
          a.description.localeCompare(b.description)
        );

        setClients(sortedData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const selectedValue = e.target.value;

    if (isLegacyMode) {
      // Legacy mode: find and set the full object
      const foundClient = clients.find(client =>
        client.id?.toString() === selectedValue?.toString()
      );
      if (setData) {
        // If selectedValue is "0", set to null or undefined
        setData(selectedValue === "0" ? null : foundClient);
      }
    } else {
      // New mode: handle both onChange and setValue
      if (onChange) {
        onChange(e);
      } else if (setValue) {
        setValue(e.target.value);
      }
    }
  };

  const selectClasses = `
    w-full 
    border 
    ${error ? 'border-red-500' : 'border-gray-300'} 
    rounded-md 
    px-3 
    py-2 
    ${!isSelected ? 'text-gray-400' : 'text-gray-700'} 
    focus:outline-none 
    focus:ring-2 
    focus:ring-blue-500 
    focus:border-blue-500 
    bg-white 
    ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-70' : 'hover:border-gray-400'} 
    transition-colors 
    duration-150
    ${className}
  `.replace(/\s+/g, ' ').trim();

  // If in legacy mode without label, return simpler version
  if (isLegacyMode && !label) {
    return (
      <div>
        <select
          disabled={disabled}
          value={displayValue}
          onChange={handleChange}
          style={{
            height: "30px",
            color: "rgb(104, 104, 104)"
          }}
          className={className}
          {...props}
        >
          <option value="0">All</option>
          {loading ? (
            <option value="" disabled>
              Loading clients...
            </option>
          ) : (
            clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.description}
              </option>
            ))
          )}
        </select>
      </div>
    );
  }

  // New structure with full styling
  return (
    <div className={`mb-1 ${parentClassName}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-500 mb-1.5">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <select
          value={displayValue}
          onChange={handleChange}
          disabled={disabled}
          className={selectClasses}
          {...props}
        >
          {loading ? (
            <option value="" disabled className="text-gray-400">
              Loading clients...
            </option>
          ) : (
            <>
              <option value="0" className="text-gray-700">{placeholder}</option>
              {clients.map((client) => (
                <option
                  key={client.id}
                  value={client.id}
                  className="text-gray-700"
                >
                  {client.description}
                </option>
              ))}
            </>
          )}
        </select>

        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
          </div>
        )}
      </div>

      {error && errorMessage && (
        <span className="mt-1 block text-sm text-red-600">{errorMessage}</span>
      )}
    </div>
  );
};
export const PayTypeIdSelectWithAll = ({
  // New props structure
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  value,
  onChange,
  setValue,
  placeholder = "Select pay type",
  className = "",
  parentClassName = "",
  error = false,
  errorMessage = "",
  isLoading = false,

  // Backward compatibility props
  data,           // For old usage: selected pay type object
  setData,        // For old usage: function to set selected pay type object
  isEditable,     // For old usage: controls editability

  ...props
}) => {
  const [payTypes, setPayTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Handle backward compatibility
  const isLegacyMode = data !== undefined || setData !== undefined;

  // Determine if disabled (support both new and legacy props)
  const disabled = isEditMode || isDisabled || loading || isLoading ||
    (isLegacyMode && !isEditable);

  // Determine value (support both new and legacy props)
  let displayValue;
  if (isLegacyMode) {
    displayValue = data?.id?.toString() || "0";
  } else {
    displayValue = value || "0";
  }

  const isSelected = displayValue && displayValue !== "0";

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          domain + "/api/Paytype/active",
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const sortedData = response.data.sort((a, b) =>
          a.description.localeCompare(b.description)
        );

        setPayTypes(sortedData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const selectedValue = e.target.value;

    if (isLegacyMode) {
      // Legacy mode: find and set the full object
      const foundPayType = payTypes.find(payType =>
        payType.id?.toString() === selectedValue?.toString()
      );
      if (setData) {
        // If selectedValue is "0", set to null or undefined
        setData(selectedValue === "0" ? null : foundPayType);
      }
    } else {
      // New mode: handle both onChange and setValue
      if (onChange) {
        onChange(e);
      } else if (setValue) {
        setValue(e.target.value);
      }
    }
  };

  const selectClasses = `
    w-full 
    border 
    ${error ? 'border-red-500' : 'border-gray-300'} 
    rounded-md 
    px-3 
    py-2 
    ${!isSelected ? 'text-gray-400' : 'text-gray-700'} 
    focus:outline-none 
    focus:ring-2 
    focus:ring-blue-500 
    focus:border-blue-500 
    bg-white 
    ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-70' : 'hover:border-gray-400'} 
    transition-colors 
    duration-150
    ${className}
  `.replace(/\s+/g, ' ').trim();

  // If in legacy mode without label, return simpler version
  if (isLegacyMode && !label) {
    return (
      <div>
        <select
          disabled={disabled}
          value={displayValue}
          onChange={handleChange}
          style={{
            height: "30px",
            color: "rgb(104, 104, 104)"
          }}
          className={className}
          {...props}
        >
          <option value="0">All</option>
          {loading ? (
            <option value="" disabled>
              Loading pay types...
            </option>
          ) : (
            payTypes.map((payType) => (
              <option key={payType.id} value={payType.id}>
                {payType.description}
              </option>
            ))
          )}
        </select>
      </div>
    );
  }

  // New structure with full styling
  return (
    <div className={`mb-1 ${parentClassName}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-500 mb-1.5">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <select
          value={displayValue}
          onChange={handleChange}
          disabled={disabled}
          className={selectClasses}
          {...props}
        >
          {loading ? (
            <option value="" disabled className="text-gray-400">
              Loading pay types...
            </option>
          ) : (
            <>
              <option value="0" className="text-gray-700">{placeholder}</option>
              {payTypes.map((payType) => (
                <option
                  key={payType.id}
                  value={payType.id}
                  className="text-gray-700"
                >
                  {payType.description}
                </option>
              ))}
            </>
          )}
        </select>

        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
          </div>
        )}
      </div>

      {error && errorMessage && (
        <span className="mt-1 block text-sm text-red-600">{errorMessage}</span>
      )}
    </div>
  );
};
export const DepartmentIdSelectWithAll = ({
  // New props structure
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  value,
  onChange,
  setValue,
  placeholder = "Select department",
  className = "",
  parentClassName = "",
  error = false,
  errorMessage = "",
  isLoading = false,

  // Backward compatibility props
  data,           // For old usage: selected department object
  setData,        // For old usage: function to set selected department object
  isEditable,     // For old usage: controls editability

  // New toggle to match EmployeeType
  allowAll = false,

  ...props
}) => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Handle backward compatibility
  const isLegacyMode = data !== undefined || setData !== undefined;

  // Determine if disabled (support both new and legacy props)
  const disabled = isEditMode || isDisabled || loading || isLoading ||
    (isLegacyMode && !isEditable);

  // Determine value (support both new and legacy props)
  let displayValue;
  if (isLegacyMode) {
    displayValue = data?.id?.toString() || "0";
  } else {
    displayValue = value || "0";
  }

  // Matching EmployeeType logic for selection state
  const isSelected = (() => {
    if (displayValue === "0") {
      return allowAll; // "All" counts as selected if allowed
    }
    return displayValue !== "0"; // any real department
  })();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          domain + "/api/Department/active",
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const sortedData = response.data.sort((a, b) =>
          a.description.localeCompare(b.description)
        );

        setDepartments(sortedData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const selectedValue = e.target.value;

    if (isLegacyMode) {
      // Legacy mode: find and set the full object
      const foundDepartment = departments.find(dept =>
        dept.id?.toString() === selectedValue?.toString()
      );
      if (setData) {
        // If selectedValue is "0", set to null or undefined
        setData(selectedValue === "0" ? null : foundDepartment);
      }
    } else {
      // New mode: handle both onChange and setValue
      if (onChange) {
        onChange(e);
      } else if (setValue) {
        setValue(selectedValue);
      }
    }
  };

  const selectClasses = `
    w-full 
    border 
    ${error ? 'border-red-500' : 'border-gray-300'} 
    rounded-md 
    px-3 
    py-2 
    ${!isSelected ? 'text-gray-400' : 'text-gray-700'} 
    focus:outline-none 
    focus:ring-2 
    focus:ring-blue-500 
    focus:border-blue-500 
    bg-white 
    ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-70' : 'hover:border-gray-400'} 
    transition-colors 
    duration-150
    ${className}
  `.replace(/\s+/g, ' ').trim();

  // If in legacy mode without label, return simpler version
  if (isLegacyMode && !label) {
    return (
      <div>
        <select
          disabled={disabled}
          value={displayValue}
          onChange={handleChange}
          className={className}
          {...props}
        >
          <option value="0">All</option>
          {loading ? (
            <option value="" disabled>
              Loading departments...
            </option>
          ) : (
            departments.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.description}
              </option>
            ))
          )}
        </select>
      </div>
    );
  }

  // New structure with full styling
  return (
    <div className={`mb-1 ${parentClassName}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-500 mb-1.5">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <select
          value={displayValue}
          onChange={handleChange}
          disabled={disabled}
          className={selectClasses}
          {...props}
        >
          {loading ? (
            <option value="" disabled className="text-gray-400">
              Loading departments...
            </option>
          ) : (
            <>
              {!allowAll && (
                <option value="0" disabled hidden>
                  {placeholder}
                </option>
              )}

              {allowAll && (
                <option value="0">
                  All
                </option>
              )}

              {departments.map((dept) => (
                <option
                  key={dept.id}
                  value={dept.id}
                  className="text-gray-700"
                >
                  {dept.description}
                </option>
              ))}
            </>
          )}
        </select>

        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
          </div>
        )}
      </div>

      {error && errorMessage && (
        <span className="mt-1 block text-sm text-red-600">{errorMessage}</span>
      )}
    </div>
  );
};
export const EmployeeTypeIdSelectWithAll = ({
  // New props structure
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  value,
  onChange,
  setValue,
  placeholder = "Select employee type",
  className = "",
  parentClassName = "",
  error = false,
  errorMessage = "",
  isLoading = false,

  // Backward compatibility props
  data,           // For old usage: selected employee type object
  setData,        // For old usage: function to set selected employee type object
  isEditable,     // For old usage: controls editability

  allowAll = false,

  ...props
}) => {
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Handle backward compatibility
  const isLegacyMode = data !== undefined || setData !== undefined;

  // Determine if disabled (support both new and legacy props)
  const disabled = isEditMode || isDisabled || loading || isLoading ||
    (isLegacyMode && !isEditable);

  // Determine value (support both new and legacy props)
  let displayValue;
  if (isLegacyMode) {
    displayValue = data?.id?.toString() || "0";
  } else {
    displayValue = value || "0";
  }

  const isSelected = (() => {
    if (displayValue === "0") {
      return allowAll; // "All" counts as selected
    }
    return displayValue !== "0"; // any real employee type
  })();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          domain + "/api/EmployeeType/active",
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const sortedData = response.data.sort((a, b) =>
          a.description.localeCompare(b.description)
        );

        setEmployeeTypes(sortedData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const selectedValue = e.target.value;

    if (isLegacyMode) {
      // Legacy mode: find and set the full object
      const foundEmployeeType = employeeTypes.find(employeeType =>
        employeeType.id?.toString() === selectedValue?.toString()
      );
      if (setData) {
        // If selectedValue is "0", set to null or undefined
        setData(selectedValue === "0" ? null : foundEmployeeType);
      }
    } else {
      // New mode: handle both onChange and setValue
      if (onChange) {
        onChange(e);
      } else if (setValue) {
        setValue(e.target.value);
      }
    }
  };

  const selectClasses = `
    w-full 
    border 
    ${error ? 'border-red-500' : 'border-gray-300'} 
    rounded-md 
    px-3 
    py-2 
    ${!isSelected ? 'text-gray-400' : 'text-gray-700'} 
    focus:outline-none 
    focus:ring-2 
    focus:ring-blue-500 
    focus:border-blue-500 
    bg-white 
    ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-70' : 'hover:border-gray-400'} 
    transition-colors 
    duration-150
    ${className}
  `.replace(/\s+/g, ' ').trim();
  // If in legacy mode without label, return simpler version
  if (isLegacyMode && !label) {
    return (
      <div>
        <select
          disabled={disabled}
          value={displayValue}
          onChange={handleChange}
          className={className}
          {...props}
        >
          <option value="0">All</option>
          {loading ? (
            <option value="" disabled>
              Loading employee types...
            </option>
          ) : (
            employeeTypes.map((employeeType) => (
              <option key={employeeType.id} value={employeeType.id}>
                {employeeType.description}
              </option>
            ))
          )}
        </select>
      </div>
    );
  }

  // New structure with full styling
  return (
    <div className={`mb-1 ${parentClassName}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-500 mb-1.5">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <select
          value={displayValue}
          onChange={handleChange}
          disabled={disabled}
          className={selectClasses}
          {...props}
        >
          {loading ? (
            <option value="" disabled className="text-gray-400">
              Loading employee types...
            </option>
          ) : (
            <>

              {!allowAll && (
                <option value="0" disabled hidden>
                  Select Employee Type
                </option>
              )}

              {allowAll && (
                <option value="0">
                  All
                </option>
              )}

              {employeeTypes.map((employeeType) => (
                <option
                  key={employeeType.id}
                  value={employeeType.id}
                  className="text-gray-700"
                >
                  {employeeType.description}
                </option>
              ))}
            </>
          )}
        </select>

        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
          </div>
        )}
      </div>

      {error && errorMessage && (
        <span className="mt-1 block text-sm text-red-600">{errorMessage}</span>
      )}
    </div>
  );
};
export const VesselIdSelectWithAll = ({
  // New props structure
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  value,
  onChange,
  setValue,
  placeholder = "Select vessel",
  className = "",
  parentClassName = "",
  error = false,
  errorMessage = "",
  isLoading = false,

  // Backward compatibility props
  data,           // For old usage: selected vessel object
  setData,        // For old usage: function to set selected vessel object
  isEditable,     // For old usage: controls editability

  allowAll = false,

  ...props
}) => {
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(false);

  const isLegacyMode = data !== undefined || setData !== undefined;
  const disabled = isEditMode || isDisabled || loading || isLoading || (isLegacyMode && !isEditable);
  let displayValue = isLegacyMode ? data?.id?.toString() || "0" : value || "0";
  const isSelected = (() => {
    if (displayValue === "0") {
      return allowAll; // "All" counts as selected
    }
    return displayValue !== "0"; // any real employee type
  })();
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(domain + "/api/Vessel/active", {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });

        const sortedData = response.data.sort((a, b) => {
          const nameA = a.name || a.description || "";
          const nameB = b.name || b.description || "";
          return nameA.localeCompare(nameB);
        });

        setVessels(sortedData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const selectedValue = e.target.value;

    if (isLegacyMode) {
      const foundVessel = vessels.find(v => v.id?.toString() === selectedValue?.toString());
      if (setData) {
        setData(selectedValue === "0" ? null : foundVessel);
      }
    } else {
      if (onChange) onChange(e);
      else if (setValue) setValue(e.target.value);
    }
  };

  const selectClasses = `
    w-full 
    border 
    ${error ? 'border-red-500' : 'border-gray-300'} 
    rounded-md 
    px-3 
    py-2 
    ${!isSelected ? 'text-gray-400' : 'text-gray-700'} 
    focus:outline-none 
    focus:ring-2 
    focus:ring-blue-500 
    focus:border-blue-500 
    bg-white 
    ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-70' : 'hover:border-gray-400'} 
    transition-colors 
    duration-150
    ${className}
  `.replace(/\s+/g, ' ').trim();

  // Legacy mode simple version
  if (isLegacyMode && !label) {
    return (
      <div>
        <select
          disabled={disabled}
          value={displayValue}
          onChange={handleChange}
          className={className}
          {...props}
        >
          <option value="0">All</option>
          {loading ? (
            <option value="" disabled>Loading vessels...</option>
          ) : (
            vessels.map(v => (
              <option key={v.id} value={v.id}>
                {v.name || v.description || `Vessel ${v.id}`}
              </option>
            ))
          )}
        </select>
      </div>
    );
  }

  return (
    <div className={`mb-1 ${parentClassName}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-500 mb-1.5">
          {label}{isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <select
          value={displayValue}
          onChange={handleChange}
          disabled={disabled}
          className={selectClasses}
          {...props}
        >
          {loading ? (
            <option value="" disabled className="text-gray-400">
              Loading vessels...
            </option>
          ) : (
            <>
              {!allowAll && (
                <option value="0" disabled hidden>
                  {placeholder}
                </option>
              )}

              {allowAll && (
                <option value="0">All</option>
              )}

              {vessels.map(v => (
                <option key={v.id} value={v.id} className="text-gray-700">
                  {v.name || v.description || `Vessel ${v.id}`}
                </option>
              ))}
            </>
          )}
        </select>

        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
          </div>
        )}
      </div>

      {error && errorMessage && (
        <span className="mt-1 block text-sm text-red-600">{errorMessage}</span>
      )}
    </div>
  );
};

export const PayrollPeriodSelectWithAll = ({
  // New props
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  value,
  onChange,
  setValue,
  placeholder = "Select payroll period",
  className = "",
  parentClassName = "",
  error = false,
  errorMessage = "",
  isLoading = false,

  // Legacy props
  data,          // legacy selected payroll period object
  setData,       // legacy setter
  isEditable,    // legacy editability

  allowAll = false,

  ...props
}) => {
  const [payrollPeriods, setPayrollPeriods] = useState([]);
  const [loading, setLoading] = useState(false);

  const isLegacyMode = data !== undefined || setData !== undefined;
  const disabled = isEditMode || isDisabled || loading || isLoading || (isLegacyMode && !isEditable);

  const displayValue = isLegacyMode ? data?.id?.toString() || "0" : value || "0";
  const isSelected = displayValue !== "0" || allowAll;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(domain + `/api/PayrollPeriod/active`, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const sortedData = response.data.sort((a, b) =>
          (b.dateFrom || "").localeCompare(a.dateFrom || "")
        );
        setPayrollPeriods(sortedData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const selectedValue = e.target.value;
    if (isLegacyMode) {
      const found = payrollPeriods.find(p => p.id?.toString() === selectedValue?.toString());
      if (setData) setData(selectedValue === "0" ? null : found);
    } else {
      if (onChange) onChange(e);
      else if (setValue) setValue(e.target.value);
    }
  };

  const selectClasses = `
    w-full 
    border 
    ${error ? 'border-red-500' : 'border-gray-300'} 
    rounded-md 
    px-3 
    py-2 
    ${!isSelected ? 'text-gray-400' : 'text-gray-700'} 
    focus:outline-none 
    focus:ring-2 
    focus:ring-blue-500 
    focus:border-blue-500 
    bg-white 
    ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-70' : 'hover:border-gray-400'} 
    transition-colors 
    duration-150
    ${className}
  `.replace(/\s+/g, ' ').trim();

  const renderOption = (period) => {
    const formattedFrom = format(new Date(period.dateFrom), 'MM/dd/yyyy');
    const formattedTo = format(new Date(period.dateTo), 'MM/dd/yyyy');
    const payType = period.payTypeDescription ? ` ${period.payTypeDescription}` : "";
    return (
      <option key={period.id} value={period.id}>
        {`${formattedFrom} - ${formattedTo}${payType}`}
      </option>
    );
  };

  // Legacy simple mode
  if (isLegacyMode && !label) {
    return (
      <div>
        <select
          disabled={disabled}
          value={displayValue}
          onChange={handleChange}
          className={className}
          {...props}
        >
          {!allowAll && <option value="0" disabled hidden>{placeholder}</option>}
          {allowAll && <option value="0">All</option>}
          {loading ? (
            <option value="" disabled>Loading payroll periods...</option>
          ) : (
            payrollPeriods.map(renderOption)
          )}
        </select>
      </div>
    );
  }

  return (
    <div className={`mb-1 ${parentClassName}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-500 mb-1.5">
          {label}{isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <select
          value={displayValue}
          onChange={handleChange}
          disabled={disabled}
          className={selectClasses}
          {...props}
        >
          {loading ? (
            <option value="" disabled className="text-gray-400">
              Loading payroll periods...
            </option>
          ) : (
            <>
              {!allowAll && <option value="0" disabled hidden>{placeholder}</option>}
              {allowAll && <option value="0">All</option>}
              {payrollPeriods.map(renderOption)}
            </>
          )}
        </select>

        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
          </div>
        )}
      </div>

      {error && errorMessage && (
        <span className="mt-1 block text-sm text-red-600">{errorMessage}</span>
      )}
    </div>
  );
};

export const ClassificationSelectWithAll = ({
  // New props
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  value,
  onChange,
  setValue,
  placeholder = "Select classification",
  className = "",
  parentClassName = "",
  error = false,
  errorMessage = "",
  isLoading = false,

  // Legacy props
  data,
  setData,
  isEditable,

  allowAll = false,
  user,
  setActiveRowIds,
  setSelectedDTR,

  ...props
}) => {
  const [classifications, setClassifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const isLegacyMode = data !== undefined || setData !== undefined;
  const disabled = isEditMode || isDisabled || loading || isLoading || (isLegacyMode && !isEditable);
  const displayValue = isLegacyMode ? data?.id?.toString() || "0" : value || "0";
  const isSelected = displayValue !== "0" || allowAll;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(domain + `/api/Classification/active/username/${user.username}`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const sortedData = response.data.sort((a, b) =>
        (a.description || "").localeCompare(b.description || "")
      );
      setClassifications(sortedData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (e) => {
    const selectedValue = e.target.value;

    if (isLegacyMode) {
      const found = classifications.find(c => c.id?.toString() === selectedValue?.toString());
      if (setData) setData(selectedValue === "0" ? null : found);
    } else {
      if (onChange) onChange(e);
      else if (setValue) setValue(e.target.value);
    }

    if (setActiveRowIds && setSelectedDTR) {
      setActiveRowIds([]);
      setSelectedDTR([]);
    }
  };

  const selectClasses = `
    w-full 
    border 
    ${error ? 'border-red-500' : 'border-gray-300'} 
    rounded-md 
    px-3 
    py-2 
    ${!isSelected ? 'text-gray-400' : 'text-gray-700'} 
    focus:outline-none 
    focus:ring-2 
    focus:ring-blue-500 
    focus:border-blue-500 
    bg-white 
    ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-70' : 'hover:border-gray-400'} 
    transition-colors 
    duration-150
    ${className}
  `.replace(/\s+/g, ' ').trim();

  if (isLegacyMode && !label) {
    return (
      <div>
        <select
          disabled={disabled}
          value={displayValue}
          onChange={handleChange}
          className={className}
          {...props}
        >
          <option value="0">All</option>
          {loading ? (
            <option value="" disabled>Loading classifications...</option>
          ) : (
            classifications.map(c => (
              <option key={c.id} value={c.id}>
                {c.description}
              </option>
            ))
          )}
        </select>
      </div>
    );
  }

  return (
    <div className={`mb-1 ${parentClassName}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-500 mb-1.5">
          {label}{isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <select
          value={displayValue}
          onChange={handleChange}
          disabled={disabled}
          className={selectClasses}
          {...props}
        >
          {loading ? (
            <option value="" disabled className="text-gray-400">
              Loading classifications...
            </option>
          ) : (
            <>
              {!allowAll && <option value="0" disabled hidden>{placeholder}</option>}
              {allowAll && <option value="0">All</option>}
              {classifications.map(c => (
                <option key={c.id} value={c.id} className="text-gray-700">
                  {c.description}
                </option>
              ))}
            </>
          )}
        </select>

        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
          </div>
        )}
      </div>

      {error && errorMessage && (
        <span className="mt-1 block text-sm text-red-600">{errorMessage}</span>
      )}
    </div>
  );
};
export function SectionIdSelectWithAll({ data, setData, isEditable }) {
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/Section/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: "rgb(104, 104, 104)"
        }}>
        <option value="0" >All</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function ClassificationIdSelectWithAll({ data, setData, isEditable }) {
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/Classification/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: "rgb(104, 104, 104)"
        }}>
        <option value="0" >All</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function CompanyIdSelectWithAll({ data, setData, isEditable }) {
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/Company/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: "rgb(104, 104, 104)"
        }}>
        <option value="0" >All</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function PositionIdSelectWithAll({ data, setData, isEditable }) {
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/Position/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: "rgb(104, 104, 104)"
        }}>
        <option value="0" >All</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function JobTitleIdSelectWithAll({ data, setData, isEditable }) {
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/JobTitle/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: "rgb(104, 104, 104)"
        }}>
        <option value="0" >All</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function EmployeeGroup1IdSelectWithAll({ data, setData, isEditable }) {
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/EmployeeGroup1/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: "rgb(104, 104, 104)"
        }}>
        <option value="0" >All</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function EmployeeGroup2IdSelectWithAll({ data, setData, isEditable }) {
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/EmployeeGroup2/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: "rgb(104, 104, 104)"
        }}>
        <option value="0" >All</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function PlantilyaIdSelectWithAll({ data, setData, isEditable }) {
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/Plantilya/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: "rgb(104, 104, 104)"
        }}>
        <option value="0" >All</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function CostCenterIdSelectWithAll({ data, setData, isEditable }) {
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/CostCenter/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: "rgb(104, 104, 104)"
        }}>
        <option value="0" >All</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function ClientIdSelectWithNone({ data, setData, isEditable }) {
  const [isSelected, setIsSelected] = useState(false);
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/Client/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    setIsSelected(true);
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
    if (data) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [data]);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)"
        }}>
        <option value="">None</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function PayTypeIdSelectWithNone({ data, setData, isEditable }) {
  const [isSelected, setIsSelected] = useState(false);
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/PayType/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    setIsSelected(true);
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
    if (data) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [data]);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)"
        }}>
        <option value="">None</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function DepartmentIdSelectWithNone({ data, setData, isEditable }) {
  const [isSelected, setIsSelected] = useState(false);
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/Department/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    setIsSelected(true);
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
    if (data) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [data]);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)"
        }}>
        <option value="">None</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function SectionIdSelectWithNone({ data, setData, isEditable }) {
  const [isSelected, setIsSelected] = useState(false);
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/Section/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    setIsSelected(true);
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
    if (data) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [data]);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)"
        }}>
        <option value="">None</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function ClassificationIdSelectWithNone({ data, setData, isEditable }) {
  const [isSelected, setIsSelected] = useState(false);
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/Classification/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    setIsSelected(true);
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
    if (data) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [data]);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)"
        }}>
        <option value="">None</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function CompanyIdSelectWithNone({ data, setData, isEditable }) {
  const [isSelected, setIsSelected] = useState(false);
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/Company/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    setIsSelected(true);
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
    if (data) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [data]);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)"
        }}>
        <option value="">None</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function PositionIdSelectWithNone({ data, setData, isEditable }) {
  const [isSelected, setIsSelected] = useState(false);
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/Position/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    setIsSelected(true);
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
    if (data) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [data]);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)"
        }}>
        <option value="">None</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function JobTitleIdSelectWithNone({ data, setData, isEditable }) {
  const [isSelected, setIsSelected] = useState(false);
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/JobTitle/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    setIsSelected(true);
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
    if (data) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [data]);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)"
        }}>
        <option value="">None</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function EmployeeGroup1IdSelectWithNone({ data, setData, isEditable }) {
  const [isSelected, setIsSelected] = useState(false);
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/EmployeeGroup1/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    setIsSelected(true);
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
    if (data) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [data]);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)"
        }}>
        <option value="">None</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function EmployeeGroup2IdSelectWithNone({ data, setData, isEditable }) {
  const [isSelected, setIsSelected] = useState(false);
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/EmployeeGroup2/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    setIsSelected(true);
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
    if (data) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [data]);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)"
        }}>
        <option value="">None</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function LocationIdSelectWithNone({ data, setData, isEditable }) {
  const [isSelected, setIsSelected] = useState(false);
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/Location/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    setIsSelected(true);
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
    if (data) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [data]);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)"
        }}>
        <option value="">None</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function PlantilyaIdSelectWithNone({ data, setData, isEditable }) {
  const [isSelected, setIsSelected] = useState(false);
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/Plantilya/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    setIsSelected(true);
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
    if (data) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [data]);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)"
        }}>
        <option value="">None</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
export function CostCenterIdSelectWithNone({ data, setData, isEditable }) {
  const [isSelected, setIsSelected] = useState(false);
  const [_data, _setData] = useState([]);

  const fetchData = async () => {
    const apiUrl = domain + "/api/CostCenter/active";

    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // You can include other headers if needed
        }
      })

      response.data.sort((a, b) => a.description.localeCompare(b.description));
      _setData(response.data); // Set the response data to the state
    } catch (error) {
      console.error('Error fetching data:', error);
    };
  }

  const handleSelectChange = (event) => {
    setIsSelected(true);
    const dataId = event.target.value;
    const foundData = _data.find(i => i.id?.toString() === dataId?.toString());

    setData(foundData);
  };

  useEffect(() => {
    fetchData();
    if (data) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [data]);

  return (
    <div>
      <select disabled={!isEditable} value={data?.id || ""} onChange={handleSelectChange}
        style={{
          height: "30px",
          color: isSelected ? "rgb(104, 104, 104)" : "rgba(0, 0, 0, 0.356)"
        }}>
        <option value="">None</option>
        {_data.map((item) => (
          <option value={item.id}>{
            item.description}
          </option>
        ))}
      </select>
    </div>
  );
};
//#endregion